import { Box, useTheme } from '@chronosphereio/chrono-ui';
import { CodeEditor, type CodeEditorProps } from '@chronosphereio/editors';
import { unifiedMergeView } from '@codemirror/merge';
import { type ReactNode, useMemo } from 'react';
import { useQueryEditorContext } from './query-editor-context';

export interface QueryEditorInputProps {
  extensions?: CodeEditorProps['extensions'];
  placeholder?: string;
  readOnly?: boolean;
  endAdornment?: ReactNode;
  /**
   * When provided, enables a unified merge/diff view showing changes between
   * this value and the current editor value. Source-agnostic — can be driven
   * by AI suggestions, version history, or any other comparison source.
   */
  previousValue?: string;
  /**
   * tab key behavior. defaults to 'focus' so query inputs don't trap keyboard
   */
  tabBehavior?: CodeEditorProps['tabBehavior'];
  /**
   * fires when the editor's content area loses focus. exposed as a react prop
   * (rather than requiring an extension) so callback identity changes don't
   * trigger a codemirror reconfigure on every keystroke.
   */
  onBlur?: () => void;
}

/**
 * CodeMirror editor wired to the nearest QueryEditor.Root context.
 *
 * Language-specific behavior (grammar, autocomplete, linting) is injected via
 * the `extensions` prop — the component itself is language-agnostic.
 */
export function QueryEditorInput({
  extensions,
  placeholder,
  readOnly,
  endAdornment,
  previousValue,
  tabBehavior = 'focus',
  onBlur,
}: QueryEditorInputProps) {
  const { theme: t } = useTheme();
  const { value, onChange, onRun } = useQueryEditorContext();

  const mergedExtensions = useMemo(() => {
    const exts = extensions ? [...extensions] : [];
    if (previousValue !== undefined) {
      exts.push(
        unifiedMergeView({
          allowInlineDiffs: false,
          mergeControls: false,
          original: previousValue,
        })
      );
    }
    return exts;
  }, [extensions, previousValue]);

  const wrappedEndAdornment =
    endAdornment != null && endAdornment !== false ? (
      <Box
        display="flex"
        alignItems="center"
        flexShrink={0}
        sx={{
          height: 32,
          marginLeft: t.spacing(1),
          gap: t.spacing(0.25),
        }}
      >
        {endAdornment}
      </Box>
    ) : undefined;

  return (
    <CodeEditor
      value={value}
      onChange={onChange}
      onRun={onRun}
      onBlur={onBlur}
      extensions={mergedExtensions}
      placeholder={placeholder}
      readOnly={readOnly}
      endAdornment={wrappedEndAdornment}
      disableBorder
      tabBehavior={tabBehavior}
    />
  );
}
